import React from 'react';
import ReactDOM from 'react-dom/client';
import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import GoogleAnalytics from './components/google-analytics';
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary';
import { router } from './router';
import { ThemeProvider } from './theme/ThemeProvider';
import { CustomMuiThemeProvider } from './theme/MuiThemeProvider';
import { PostHogProvider } from 'posthog-js/react';
import './index.css';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const enableAnalytics =
  import.meta.env.VITE_APP_ENV === 'production' ||
  import.meta.env.VITE_ENABLE_ANALYTICS === 'true';

const AppProviders = () => {
  const redirectUri = window.location.origin + '/auth/callback';

  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        // Use runtime origin to avoid env mismatch causing lost callbacks
        redirect_uri: redirectUri,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access',
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      skipRedirectCallback={false}
      onRedirectCallback={(appState) => {
        // Store the returnTo target for the callback page to use AFTER registration
        try {
          const storedReturnTo = sessionStorage.getItem('auth_returnTo');
          const safe = (path?: string | null) =>
            path && path.startsWith('/') ? path : null;
          const target =
            safe(appState?.returnTo) || safe(storedReturnTo) || '/dashboard';

          // Store the final destination - the callback page will navigate there after registration
          sessionStorage.setItem('auth_final_destination', target);

          // DON'T navigate yet - let the callback page render and register the user first
        } catch (error) {
          console.error('Auth callback error:', error);
          // Fallback - still let callback page handle it
          sessionStorage.setItem('auth_final_destination', '/dashboard');
        }
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <CustomMuiThemeProvider>
            <RouterProvider
              router={router}
              future={{
                v7_startTransition: true,
              }}
            />
          </CustomMuiThemeProvider>
        </ThemeProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </Auth0Provider>
  );
};

// HMR-safe singleton root + optional StrictMode toggle
declare global {
  interface Window {
    __KEYSHA_ROOT__?: ReturnType<typeof ReactDOM.createRoot>;
  }
}

const container = document.getElementById('root')!;

const AppTree = (
  <GlobalErrorBoundary>
    <HelmetProvider>
      <GoogleAnalytics />
      {enableAnalytics ? (
        <PostHogProvider
          apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
          options={{
            api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
            capture_exceptions: true,
            debug: import.meta.env.MODE === 'development',
          }}
        >
          <AppProviders />
        </PostHogProvider>
      ) : (
        <AppProviders />
      )}
    </HelmetProvider>
  </GlobalErrorBoundary>
);

const useStrictMode = import.meta.env.VITE_STRICT_MODE !== 'false';
const RootTree = useStrictMode ? (
  <React.StrictMode>{AppTree}</React.StrictMode>
) : (
  AppTree
);

if (window.__KEYSHA_ROOT__) {
  // Reuse existing root (prevents duplicate createRoot on HMR)
  window.__KEYSHA_ROOT__!.render(RootTree);
} else {
  const root = ReactDOM.createRoot(container);
  window.__KEYSHA_ROOT__ = root;
  root.render(RootTree);
}
